import { useEffect } from "react"
import { PUBLISH_SITES, SITE_API_ENABLED, SITE_API_NOTE } from "../api/config"
import { useRigaCatalogs } from "../api/queries"
import type { Catalog, WebsiteIdentity } from "../api/types"
import { useAccess } from "../auth/rights"
import { Note } from "../components/Note"
import { usePublishTarget } from "../components/site/usePublishTarget"
import { useStoredNumber } from "../hooks/useStored"
import { StorageKey } from "../lib/storage"
import CatalogComposition from "./publish/CatalogComposition"
import CreateSiteOrCatalog from "./publish/CreateSiteOrCatalog"
import PartialPanel from "./publish/PartialPanel"

/**
 * Загрузка на сайт — экран администратора.
 *
 * Массовую заливку и загрузку одной группы отсюда убрали (25 августа): первая
 * годами стояла выключенной (`VIA_RU_NOTE` — метод есть только в российском
 * `ecm`, а он роняет процесс при обращении к базе сайта из России), вторая
 * дублировала вкладку «Сайт» карточки группы (`GroupSitePanel.tsx`) один в
 * один. Осталось три блока:
 *
 * 1. **Точечное обновление** — сверяет уже загруженный сайт с ERP, не очищая
 *    таблицы. Тоже выключено `VIA_RU_NOTE`, ждёт рижского двойника
 *    (`PLAN.md`, фаза 7-Л) — но выбор, что именно и где меняется, сделан явным
 *    прямо в панели, а не только в шапке страницы.
 * 2. **Каталоги** — состав каталога (какие группы в нём сейчас) и назначение
 *    товаров: добавление конкретных групп из одного каталога в другой, а не
 *    только целиком, как раньше.
 * 3. **Создать сайт или каталог** — заводит новый сайт (со всеми реквизитами
 *    ФТП и БД) или печатный каталог прямо из интерфейса.
 *
 * Списки сайтов и каталогов для блоков 2 и 3 — `useRigaCatalogs()`: только то,
 * что касается Риги (свои сайты и печатные каталоги), а не все ~30 каталогов
 * ERP.
 */
export default function Publish() {
	const { canPublish, isAdmin } = useAccess()
	const rigaCatalogs = useRigaCatalogs()

	const [catalogId, setCatalogId] = useStoredNumber(
		StorageKey.publishCatalog,
		null
	)

	// Каталог-источник сбрасываем при смене сайта: по умолчанию грузим на сайт
	// его собственный каталог, иначе на сайт уедет чужой набор товаров.
	const { sites, site, siteCatalog, selectSite, isLoading } = usePublishTarget(
		() => setCatalogId(null)
	)

	useEffect(() => {
		if (!siteCatalog) return
		const known = (rigaCatalogs.data ?? []).some(item => item.id === catalogId)
		if (catalogId !== null && known) return
		setCatalogId(siteCatalog.id)
	}, [siteCatalog, rigaCatalogs.data, catalogId, setCatalogId])

	const catalog =
		(rigaCatalogs.data ?? []).find(item => item.id === catalogId) ?? null

	if (isLoading || rigaCatalogs.isLoading) {
		return (
			<div className="pub">
				<span className="spinner" />
			</div>
		)
	}

	return (
		<div className="pub">
			<SiteBar
				sites={sites}
				site={site}
				onSite={selectSite}
				catalogs={rigaCatalogs.data ?? []}
				catalog={catalog}
				siteCatalog={siteCatalog}
				onCatalog={setCatalogId}
			/>

			{!site && (
				<Note tone="error">
					Ни один из сайтов загрузки не найден в справочнике `WebSites`:{" "}
					{PUBLISH_SITES.join(", ")}. Пока сайт туда не заведён (а к нему —
					каталог с тем же именем), публиковать некуда: бэк ищет и сайт, и
					каталог по имени.
				</Note>
			)}
			{site && !siteCatalog && (
				<Note tone="error">
					В справочнике каталогов нет каталога с именем «{site.name}». Бэк ищет
					его именно по имени сайта, без него публикация не работает — правится
					на российской стороне.
				</Note>
			)}

			{site && siteCatalog && catalog && (
				<>
					{!SITE_API_ENABLED && (
						<Note tone="warn">
							<b>Операции с сайтом выключены.</b> {SITE_API_NOTE} Со стенда
							работает всё, что живёт в MSSQL: состав каталога, назначение
							товаров, проверка готовности.
						</Note>
					)}

					{!canPublish && (
						<Note tone="hint">
							Загрузка на сайт доступна ролям «Контент-менеджер Латвии»,
							«Контент-менеджер Риги» и «Администратор». Экран открыт на чтение.
						</Note>
					)}

					<PartialPanel site={site} catalog={catalog} />
				</>
			)}

			<CatalogComposition
				catalogs={rigaCatalogs.data ?? []}
				defaultCatalogId={siteCatalog?.id ?? null}
				disabled={!canPublish}
			/>

			{isAdmin && <CreateSiteOrCatalog />}
		</div>
	)
}

function SiteBar({
	sites,
	site,
	onSite,
	catalogs,
	catalog,
	siteCatalog,
	onCatalog,
}: {
	sites: WebsiteIdentity[]
	site: WebsiteIdentity | null
	onSite: (id: number) => void
	catalogs: Catalog[]
	catalog: Catalog | null
	siteCatalog: Catalog | null
	onCatalog: (id: number) => void
}) {
	return (
		<div className="pub-bar">
			<label className="pub-bar__field">
				<span className="pub-bar__label">Сайт</span>
				<select
					className="select"
					value={site?.id ?? ""}
					onChange={event => onSite(Number(event.target.value))}
				>
					{sites.map(item => (
						<option key={item.id} value={item.id}>
							{item.name}
						</option>
					))}
				</select>
			</label>

			<label className="pub-bar__field">
				<span className="pub-bar__label">Каталог для точечного обновления</span>
				<select
					className="select"
					value={catalog?.id ?? ""}
					onChange={event => onCatalog(Number(event.target.value))}
				>
					{catalogs.map(item => (
						<option key={item.id} value={item.id}>
							{item.name}
							{siteCatalog && item.id === siteCatalog.id
								? " — каталог сайта"
								: ""}
						</option>
					))}
				</select>
			</label>

			<span className="pub-bar__spacer" />

			{site && (
				<span className="muted mono">
					сайт {site.id} · каталог {catalog?.id ?? "—"}
				</span>
			)}
		</div>
	)
}
